import React from "react";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRefCapture } from "./hooks/useRefCapture";
import { AuthProvider, useAuth, useCurrentUser } from "./auth";
import Layout from "./components/Layout";
import RequirePermission from "./components/RequirePermission";
import ErrorBoundary from "./components/ErrorBoundary";
import { Alert, Button, Loading } from "./components/ui";
import {
  ORGANIZATION_EDIT_PERMISSION,
  ORGANIZATION_LIST_USERS_PERMISSION,
  PRINTER_JOB_LABEL_PERMISSION,
  PRINTER_LIST_PERMISSION,
} from "./constants/permissions";
import Settings from "./pages/Settings";
import OrganizationSetupInfo from "./pages/OrganizationSetupInfo";
import Products from "./pages/Products";
import NewProduct from "./pages/NewProduct";
import ProductDetails from "./pages/ProductDetails";
import Printing from "./pages/Printing";
import NotFound from "./pages/NotFound";
import BillingSubscribe from "./pages/BillingSubscribe";
import BillingInfo from "./pages/BillingInfo";
import BillingReturn from "./pages/BillingReturn";
import Welcome from "./pages/Welcome";

const returnToKey = "validlabels:return-to";
function callbackDestination() {
  try {
    const path = sessionStorage.getItem(returnToKey);
    sessionStorage.removeItem(returnToKey);
    if (
      path?.startsWith("/") &&
      !path.startsWith("//") &&
      !path.includes("\\") &&
      !path.startsWith("/callback")
    )
      return path;
  } catch {
    /* A blocked session store does not block sign-in. */
  }
  return "/products";
}
function Callback() {
  const { authenticated, isLoading, login } = useAuth();
  const { t } = useTranslation("ui");
  const destination = React.useRef<string>();
  if (isLoading) return <Loading label={t("authLoading")} />;
  if (authenticated) {
    destination.current ||= callbackDestination();
    return <Navigate to={destination.current} replace />;
  }
  return (
    <section className="vl-public-content">
      <h1>{t("authFailed")}</h1>
      <p>{t("authFailedText")}</p>
      <Button onClick={login}>{t("retry")}</Button>
    </section>
  );
}
function RequireAuth({
  children,
  workspace = false,
}: {
  children: React.ReactNode;
  workspace?: boolean;
}) {
  const { authenticated, isLoading, login } = useAuth();
  const {
    currentUser,
    isCurrentUserLoading,
    reloadCurrentUser,
    organizationSetupRequired,
    permissions,
  } = useCurrentUser();
  const { t } = useTranslation("ui");
  const location = useLocation();
  if (isLoading) return <Loading />;
  if (!authenticated)
    return (
      <Welcome
        onLogin={() => {
          try {
            sessionStorage.setItem(
              returnToKey,
              location.pathname + location.search,
            );
          } catch {
            /* optional */
          }
          login();
        }}
      />
    );
  if (isCurrentUserLoading) return <Loading label={t("profileLoading")} />;
  if (!currentUser)
    return (
      <Alert variant="error" title={t("profileError")}>
        <p>{t("profileErrorText")}</p>
        <Button variant="outline" onClick={() => void reloadCurrentUser()}>
          {t("retry")}
        </Button>
      </Alert>
    );
  if (workspace && organizationSetupRequired)
    return (
      <Navigate
        to={
          permissions.includes(ORGANIZATION_EDIT_PERMISSION)
            ? "/organization/setup"
            : "/organization/setup-info"
        }
        replace
      />
    );
  return <>{children}</>;
}
function LegacyTranslation() {
  const { id, language } = useParams();
  return (
    <Navigate
      to={`/products/${encodeURIComponent(id || "")}?${language ? `language=${encodeURIComponent(language)}&edit=1` : "add=1"}`}
      replace
    />
  );
}
function AppRoutes() {
  const location = useLocation();
  useRefCapture(location);
  return (
    <Layout>
      <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/" element={<Navigate to="/products" replace />} />
          <Route path="/callback" element={<Callback />} />
          <Route
            path="/dashboard"
            element={<Navigate to="/products" replace />}
          />
          <Route
            path="/products"
            element={
              <RequireAuth workspace>
                <RequirePermission permission="product.list">
                  <Products />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/products/new"
            element={
              <RequireAuth workspace>
                <RequirePermission permission="product.create">
                  <NewProduct />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/products/:id"
            element={
              <RequireAuth workspace>
                <RequirePermission permission="product.get">
                  <ProductDetails />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/products/:id/translations/new"
            element={<LegacyTranslation />}
          />
          <Route
            path="/products/:id/translations/:language/edit"
            element={<LegacyTranslation />}
          />
          <Route
            path="/printing"
            element={
              <RequireAuth workspace>
                <RequirePermission
                  permission={[
                    PRINTER_JOB_LABEL_PERMISSION,
                    PRINTER_LIST_PERMISSION,
                  ]}
                >
                  <Printing />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <Settings />
              </RequireAuth>
            }
          />
          <Route
            path="/organization/setup"
            element={
              <RequireAuth>
                <RequirePermission
                  permission={ORGANIZATION_EDIT_PERMISSION}
                  fallbackPath="/organization/setup-info"
                >
                  <Navigate to="/settings?section=organization" replace />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/organization/setup-info"
            element={
              <RequireAuth>
                <OrganizationSetupInfo />
              </RequireAuth>
            }
          />
          <Route
            path="/users"
            element={
              <RequireAuth>
                <RequirePermission
                  permission={ORGANIZATION_LIST_USERS_PERMISSION}
                >
                  <Navigate to="/settings?section=team" replace />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/billing/store"
            element={
              <RequireAuth>
                <Navigate to="/settings?section=billing" replace />
              </RequireAuth>
            }
          />
          <Route
            path="/billing/subscribe"
            element={
              <RequireAuth>
                <RequirePermission
                  permission={ORGANIZATION_EDIT_PERMISSION}
                  fallbackPath="/billing/info"
                >
                  <BillingSubscribe />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/billing/success"
            element={
              <RequireAuth>
                <BillingReturn />
              </RequireAuth>
            }
          />
          <Route
            path="/billing/cancel"
            element={
              <RequireAuth>
                <BillingReturn cancelled />
              </RequireAuth>
            }
          />
          <Route
            path="/billing/cancelled"
            element={<Navigate to="/billing/cancel" replace />}
          />
          <Route
            path="/billing/info"
            element={
              <RequireAuth>
                <BillingInfo />
              </RequireAuth>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    </Layout>
  );
}
export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
